import { concealFrame } from './conceal';

type Slot =
  | { kind: 'audio'; planes: Float32Array[] }
  | { kind: 'pending' }
  | { kind: 'conceal'; index: number };

/** Roughly a second of 20 ms frames — far more than any decoder should be behind. */
const MAX_QUEUE_DEPTH = 48;

export interface OutputQueueOptions {
  /** Where finished frames go, always in sender order. */
  emit(planes: Float32Array[]): void;
  /** Shape used for concealment when nothing has been decoded yet. */
  fallbackChannels: number;
  fallbackSamples: number;
}

/**
 * Keeps decoded audio in sender order.
 *
 * The WebCodecs decoder is asynchronous, so a concealment frame generated the
 * instant a gap is spotted would otherwise overtake packets still inside the
 * decoder. Every frame therefore takes a numbered slot: real packets reserve a
 * `pending` slot that the decoder callback fills in, concealment reserves a slot
 * that is synthesised at drain time from whatever actually played last. Nothing
 * leaves the queue until the slot in front of it is complete.
 */
export class OutputQueue {
  private slots: Slot[] = [];
  private lastPlanes: Float32Array[] | null = null;
  private options: OutputQueueOptions;

  constructor(options: OutputQueueOptions) {
    this.options = options;
  }

  setFallbackShape(channels: number, samples: number): void {
    this.options.fallbackChannels = channels;
    this.options.fallbackSamples = samples;
  }

  /** Frames waiting on the decoder. */
  get pendingCount(): number {
    return this.slots.reduce((n, slot) => (slot.kind === 'pending' ? n + 1 : n), 0);
  }

  get length(): number {
    return this.slots.length;
  }

  /** Reserve a slot for a packet handed to the decoder. */
  reserve(): void {
    this.slots.push({ kind: 'pending' });
    // A packet the decoder silently swallows would block the queue forever, so an
    // unreasonably deep queue drops its head rather than muting the stream.
    if (this.slots.length > MAX_QUEUE_DEPTH) {
      this.slots.shift();
      this.drain();
    }
  }

  /** Queue already-decoded audio, e.g. a PCM_F32LE frame. */
  enqueue(planes: Float32Array[]): void {
    this.slots.push({ kind: 'audio', planes });
    this.drain();
  }

  /** Reserve a slot for the `index`-th consecutive missing frame. */
  reserveConcealment(index: number): void {
    this.slots.push({ kind: 'conceal', index });
    this.drain();
  }

  /** Deliver decoder output into the earliest unfilled slot. */
  fulfil(planes: Float32Array[]): void {
    const index = this.slots.findIndex((entry) => entry.kind === 'pending');
    if (index === -1) {
      // Output with no reservation: the decoder emitted more frames than we fed it
      // (an Opus packet may carry several). Play it rather than lose audio.
      this.slots.push({ kind: 'audio', planes });
    } else {
      this.slots[index] = { kind: 'audio', planes };
    }
    this.drain();
  }

  /** Throw away everything queued — used on discontinuity and on decoder reset. */
  clear(): void {
    this.slots = [];
  }

  /** Forget the concealment source too, e.g. when the format changes. */
  reset(): void {
    this.clear();
    this.lastPlanes = null;
  }

  private drain(): void {
    while (this.slots.length > 0) {
      const slot = this.slots[0];
      if (!slot || slot.kind === 'pending') return;
      this.slots.shift();

      if (slot.kind === 'audio') {
        this.remember(slot.planes);
        this.options.emit(slot.planes);
      } else {
        const planes = concealFrame(
          this.lastPlanes,
          slot.index,
          this.options.fallbackSamples,
          this.options.fallbackChannels,
        );
        this.options.emit(planes);
      }
    }
  }

  /**
   * Copy the frame into a reusable scratch buffer before it is handed on — the
   * caller transfers the real buffers into the AudioWorklet, which detaches them,
   * and concealment needs something to work from afterwards.
   */
  private remember(planes: Float32Array[]): void {
    const length = planes[0]?.length ?? 0;
    if (length === 0 || planes.length === 0) return;

    const current = this.lastPlanes;
    if (!current || current.length !== planes.length || (current[0]?.length ?? -1) !== length) {
      this.lastPlanes = planes.map((plane) => Float32Array.from(plane));
      return;
    }
    for (let c = 0; c < planes.length; c++) {
      const target = current[c];
      const source = planes[c];
      if (target && source) target.set(source);
    }
  }
}
